import { http } from "@/services/http";
import qs from "qs";
import { cloneDeep } from "lodash";
import {
  mapModuleFilters,
  mapModuleFilterState,
  mapModuleFiltersAcl,
  mapModuleFilterQueryParameters,
} from "@/utils/ModuleFilterMapping";
import { mapModules } from "@/utils/ModuleMapping";
import {
  moduleColumnDefinition,
  multiLengthColumnDefinition,
} from "@/utils/ColumnMapping";
import { formatNumber } from "@/utils/FormatUtils";
import { unsetFilters } from "@/utils/FilterUtils";
import saveAs from "file-saver";
import { RTH_MODULE_CALCULATIONS } from "@/service/Constants";

/**
 * Store for the state of (led) modules
 *
 * @type Object
 */
const state = {
  module: null,
  moduleFilters: null,
  inputLock: "current",
  inputLockRTH: "tc",
  moduleFlux: null,
  moduleCurrent: null,
  luminaireFlux: null,
  lor: 100,
  moduleTemperature: null,
  moduleTa: 25,
  moduleRTH: null,
  canRTH: false,
  moduleSearchTerm: null,
  filters: [],
  rawFilters: [],
  modules: [],
  moduleFamilies: [],
  initialFilters: [],
  moduleColumnVisibility: [],
  pinnedModules: [],
  comparedModules: [],
  columnDefs: [],
  multiLength: false,
  multiLengthColumnDefs: [],
  lastModuleCalculationParameters: {},
  selectedMultiLengthModule: null,
  incompatibleModule: null,
};

const getters = {
  initialColumnDefs: (state, getters, rootState, rootGetters) => {
    return moduleColumnDefinition(
      rootState.brain.region,
      rootGetters["brain/auth/canI"](RTH_MODULE_CALCULATIONS)
    );
  },
  columnDefs: (state) => {
    return state.columnDefs;
  },
  initialMultiLengthColumnDefs: (state, getters, rootState) => {
    return multiLengthColumnDefinition(rootState.brain.region);
  },
  multiLengthColumnDefs: (state) => {
    return state.multiLengthColumnDefs;
  },
  filters: (state, getters, rootState, rootGetters) => {
    return mapModuleFiltersAcl(
      state.filters,
      rootGetters["brain/auth/aclFeatures"]
    );
  },
  rawFilters: (state) => {
    return state.rawFilters;
  },
  emptyInitialFilters(state) {
    return unsetFilters(state.initialFilters);
  },
  lor(state) {
    return state.lor;
  },
  pinnedModules: (state) => {
    return state.pinnedModules;
  },
  comparedModules: (state) => {
    return state.comparedModules;
  },
  modules: (state) => {
    return state.modules;
  },
  module: (state) => {
    return state.module;
  },
  moduleFlux: (state) => {
    return state.moduleFlux;
  },
  moduleCurrent: (state) => {
    return state.moduleCurrent;
  },
  luminaireFlux: (state) => {
    return state.luminaireFlux;
  },
  moduleTemperature: (state) => {
    return state.moduleTemperature;
  },
  moduleTa: (state) => {
    return state.moduleTa;
  },
  moduleRTH: (state) => {
    return state.moduleRTH;
  },
  inputLock: (state) => {
    return state.inputLock;
  },
  inputLockRTH: (state) => {
    return state.inputLockRTH;
  },
  selectedMultiLengthModule: (state) => {
    return state.selectedMultiLengthModule;
  },
  incompatibleModule: (state) => {
    return state.incompatibleModule;
  },
  canRTH: (state) => {
    return state.canRTH;
  },
  multiLength: (state) => {
    return state.multiLength;
  },
};

const mutations = {
  setColumnDefs: (state, columnDefs) => {
    state.columnDefs = [...columnDefs];
  },

  setMultiLengthColumnDefs: (state, multiLengthColumnDefs) => {
    state.multiLengthColumnDefs = [...multiLengthColumnDefs];
  },
  setMultiLength: (state, multiLength) => {
    state.multiLength = multiLength;
  },

  setFilters: (state, filters) => {
    if (filters !== null) {
      state.filters = [...filters];
    }
  },
  setRawFilters: (state, rawFilters) => {
    state.rawFilters = rawFilters;
  },
  setModuleSearchTerm: (state, searchTerm) => {
    state.moduleSearchTerm = searchTerm;
  },
  setLuminaireFlux: (state, value) => {
    state.luminaireFlux = value;
  },
  setLor: (state, value) => {
    state.lor = value;
  },
  resetSpecificFilterGroup: (state, specificFilterGroup) => {
    const targetInitialFilterGroupIndex = state.initialFilters.findIndex(
      (initialFilter) => initialFilter.title === specificFilterGroup.title
    );
    state.filters[targetInitialFilterGroupIndex] = specificFilterGroup;
  },

  setInitialFilters: (state, filters) => {
    state.initialFilters = filters;
  },

  updateFilter: (state, { update, showArchive }) => {
    state.filters.forEach((group) => {
      group.filters.forEach((filter) => {
        if (
          !showArchive &&
          filter.name === "release_state" &&
          filter.value &&
          filter.value.includes("Archived")
        ) {
          filter.value = filter.value.filter((f) => f !== "Archived");
        }

        if (update && filter.name === update.name) {
          if (
            filter.name === "housingWidth" ||
            filter.name === "housingHeight"
          ) {
            filter.value = {
              min: update.min,
              max: update.max,
            };
          } else if (filter.name === "searchTermFilter") {
            state.moduleSearchTerm = update.value;
            filter.value = update.value;
          } else {
            filter.value =
              update.values instanceof Array
                ? [...update.values]
                : update.values;
          }
        }
      });
    });
  },
  setModules: (state, modules) => {
    state.modules = [...modules];
  },

  setModuleFamilies: (state, moduleFamilies) => {
    state.moduleFamilies = [...moduleFamilies];
  },

  setModuleFlux: (state, moduleFlux) => {
    state.moduleFlux = moduleFlux;
  },

  setModuleCurrent: (state, moduleCurrent) => {
    state.moduleCurrent = moduleCurrent;
  },

  setModuleTemperature: (state, moduleTemperature) => {
    state.moduleTemperature = moduleTemperature;
  },

  setModuleTa: (state, moduleTa) => {
    state.moduleTa = moduleTa;
  },

  setModuleRTH: (state, moduleRTH) => {
    state.moduleRTH = moduleRTH;
  },

  setCanRTH: (state, canRTH) => {
    state.canRTH = canRTH;
  },

  saveModuleCalculationParameters: (state) => {
    state.lastModuleCalculationParameters = {
      moduleFlux: state.moduleFlux,
      moduleCurrent: state.moduleCurrent,
      luminaireFlux: state.luminaireFlux,
      lor: state.lor,
      moduleTemperature: state.moduleTemperature,
      inputLock: state.inputLock,
      moduleTa: state.moduleTa,
      moduleRTH: state.moduleRTH,
      inputLockRTH: state.inputLockRTH,
    };
  },

  resetModuleCalculationParameters: (state) => {
    ({
      moduleFlux: state.moduleFlux,
      moduleCurrent: state.moduleCurrent,
      luminaireFlux: state.luminaireFlux,
      lor: state.lor,
      moduleTemperature: state.moduleTemperature,
      inputLock: state.inputLock,
      moduleTa: state.moduleTa,
      moduleRTH: state.moduleRTH,
      inputLockRTH: state.inputLockRTH,
    } = state.lastModuleCalculationParameters);
  },

  setInputLock: (state, inputLock) => {
    state.inputLock = inputLock;
  },

  setInputLockRTH: (state, inputLock) => {
    state.inputLockRTH = inputLock;
  },

  setModule: (state, module) => {
    // Module that is currently highlighted in the table
    state.module = cloneDeep(module);

    // Store filterset that was used to get that result
    state.moduleFilters = cloneDeep(state.filters);

    if (module && module.calculationResult) {
      if (state.multiLength) {
        state.luminaireFlux = formatNumber(
          module.calculationResult.outputFluxOfArray * (state.lor / 100),
          "0"
        );
      }
      // Copy Flux, Current and TC to calculation form
      state.moduleFlux = formatNumber(
        module.calculationResult.outputFluxOfArray,
        "0"
      );
      state.moduleCurrent = formatNumber(
        module.calculationResult.operatingCurrent * 1000,
        "0"
      );
      state.moduleTemperature = module.calculationResult.temperature;

      if (state.lor) {
        state.luminaireFlux = Math.round(state.moduleFlux * (state.lor / 100));
      }

      if (
        module.calculationParameters !== null &&
        module.calculationParameters.tAmbient !== null
      ) {
        state.moduleTa = module.calculationParameters.tAmbient;
      } else {
        state.moduleTa = 25;
      }

      if (
        module.calculationParameters !== null &&
        module.calculationParameters.luminaireLor !== null
      ) {
        state.lor = module.calculationParameters.luminaireLor * 100;
      } else {
        state.lor = 100;
      }

      state.moduleRTH = module.calculationResult.rthHeatsinkToAmbient;
      mutations.saveModuleCalculationParameters(state);
    }
  },
  setPinnedModules: (state, pinnedModules) => {
    state.pinnedModules = [...pinnedModules];
  },
  pinModule: (state, module) => {
    state.pinnedModules.push(cloneDeep(module));
  },
  unpinModule: (state, module) => {
    const index = state.pinnedModules.findIndex((m) => m.uid === module.uid);
    if (index !== -1) {
      state.pinnedModules.splice(index, 1);
    }
  },
  setComparedModules: (state, comparedModules) => {
    state.comparedModules = [...comparedModules];
  },
  addToComparison: (state, module) => {
    if (
      !state.comparedModules.some(
        (m) => m.uid === module.uid && calculateParametersCheck(m, module)
      )
    ) {
      state.comparedModules.push(cloneDeep(module));
    }
  },
  removeFromComparison: (state, module) => {
    const index = state.comparedModules.findIndex(
      (m) => m.uid === module.uid && calculateParametersCheck(m, module)
    );
    if (index !== -1) {
      state.comparedModules.splice(index, 1);
    }
  },
  setSelectedMultiLengthModule: (state, selectedMultiLengthModule) => {
    state.selectedMultiLengthModule = selectedMultiLengthModule;
  },
  setIncompatibleModule: (state, incompatibleModule) => {
    state.incompatibleModule = cloneDeep(incompatibleModule);
  },
};

const actions = {
  /**
   * Get list of modules after calculation
   * @param rootState
   * @param rootGetters
   * @param state
   * @param commit
   * @param dispatch
   * @returns {Promise<*>}
   */
  async getModules({ rootState, rootGetters, state, commit, dispatch }) {
    commit("brain/global/setBusy", true, { root: true });

    // If a solution is chosen use it in the headers
    dispatch("brain/solutions/setHeaders", null, { root: true });

    // create the filterset to apply
    const filters = mapModuleFilterState(state);

    // and request the list
    const result = await http
      .post(
        "/api/v2/modules/data" +
          mapModuleFilterQueryParameters(rootGetters["brain/global/filters"]),
        qs.stringify(filters, { indices: false, arrayFormat: "brackets" }),
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      )
      .then((response) => response.data.data)
      .then((data) => {
        // Set Filters
        commit(
          "setFilters",
          mapModuleFilters(rootState.brain.region, data.filters)
        );

        // Set Raw Filters
        commit("setRawFilters", data.filters);

        dispatch("brain/global/setTagFamilies", null, { root: true });

        //Save this base state in the history for later undo/redo.
        if (!state.initialFilters.length) {
          commit(
            "setInitialFilters",
            mapModuleFilters(rootState.brain.region, data.filters)
          );
        }
        // Map Modules
        const modules = mapModules(rootState.brain.region, data.hits);
        commit("setModules", modules);

        if (state.module !== null) {
          const activeModule = modules.find(
            (module) => module.uid === state.module.uid
          );
          if (activeModule !== undefined) {
            commit("setModule", activeModule);
          }
        }

        return modules;
      })
      .catch((err) => {
        commit("brain/global/setBusy", false, { root: true });
        if (err.response !== undefined && err.response.status === 401) {
          this._vm.$snotify["error"]("Token expired, please log in again.");
        } else {
          this._vm.$snotify["error"](err);
        }
      });
    commit("brain/global/setBusy", false, { root: true });
    return result;
  },

  canRTH({ commit, rootGetters, state }) {
    commit(
      "setCanRTH",
      rootGetters["brain/auth/canI"](RTH_MODULE_CALCULATIONS) &&
        !state.multiLength
    );
  },

  async calculateMultiLengthModules({ getters, rootGetters, commit }) {
    const parameters = {
      luminaireLor: getters.lor,
      temperature: getters.moduleTemperature,
      // Required to disambiguate modules mapped to both voltage types (combined HV/LV modules)
      voltageType: rootGetters["brain/solutions/multiLengthVoltageType"],
    };
    if (getters.inputLock === "flux") {
      parameters["targetFlux"] = getters.moduleFlux;
      parameters["luminaireLumens"] = getters.luminaireFlux;
    } else if (getters.inputLock === "current") {
      parameters["targetCurrent"] = getters.moduleCurrent / 1000;
    }

    const system = rootGetters["brain/solutions/multiLengthModules"].map(
      (module) => {
        return { uid: module.uid, amount: module.amount };
      }
    );

    let virtualModule = null;
    await http({
      method: "POST",
      url: "/api/v2/modules/multi-length/",
      data: {
        system: system,
        parameters: parameters,
      },
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((response) => {
        virtualModule = response.data.data.system;
        commit("brain/solutions/setVirtualModule", virtualModule, {
          root: true,
        });
        commit("setModule", virtualModule);
        commit("brain/solutions/setModule", virtualModule, { root: true });
        commit("brain/drivers/setInputForm", virtualModule, {
          root: true,
        });
      })
      .catch(() => {
        this._vm.$snotify["error"]("Invalid calculation.");
      });
  },

  async getAllModuleFamilies({ commit }) {
    const result = await http.get(`/api/v2/modulefamily/all`);
    commit("setModuleFamilies", result.data.data);
    return result;
  },

  async getAllReadableModuleFamilies({ commit }) {
    const result = await http.get(`/api/v2/modulefamily/all-readable`);
    commit("setModuleFamilies", result.data.data);
    return result;
  },

  async handleFilterChange({ dispatch, commit, rootGetters }, { data }) {
    commit("updateFilter", {
      update: data,
      showArchive: rootGetters["brain/global/showArchive"],
    });
    return await dispatch("getModules");
  },

  async resetAllFilters({ dispatch }) {
    await dispatch("resetFilters");
    return await dispatch("getModules");
  },

  async resetFilters({ commit, rootState }) {
    commit("setModuleSearchTerm", null);
    const rawModuleFilters = await http({
      method: "POST",
      url: "/api/v2/modules/data?hasMatchingDriver=true&whitelistingEnabled=true&showArchive=false&size=0",
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }).then((response) => {
      return response.data.data.filters;
    });

    commit(
      "setFilters",
      mapModuleFilters(rootState.brain.region, rawModuleFilters)
    );
  },

  async downloadModules({ rootGetters, state, commit }) {
    commit("brain/global/setBusy", true, { root: true });
    // create the filterset to apply
    const filters = mapModuleFilterState(state);

    // and request the list
    const result = await http
      .post(
        "api/v2/modules/exportxlsx" +
          mapModuleFilterQueryParameters(rootGetters["brain/global/filters"]),
        qs.stringify(filters, { indices: false, arrayFormat: "brackets" }),
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          responseType: "arraybuffer",
        }
      )
      .then((response) => {
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;",
        });
        const date = new Date();
        const filename = "modules-export-" + date.getTime();

        saveAs(blob, filename + ".xlsx");
      })
      .catch((err) => {
        if (err.response !== undefined && err.response.status === 401) {
          this._vm.$snotify["error"]("Token expired, please log in again.");
        } else {
          this._vm.$snotify["error"](err);
        }
      });
    commit("brain/global/setBusy", false, { root: true });
    return result;
  },

  setFilters({ commit }, filters) {
    commit("setFilters", filters);
    const searchFilter = filters.find(
      (filter) => filter.title === "Exceptions"
    );
    if (searchFilter) {
      if (searchFilter.filters[0].value != null) {
        commit("setModuleSearchTerm", searchFilter.filters[0].value);
      }
    }
  },

  clearCalculatedPerformance({ commit }) {
    commit("setModuleTemperature", null);
    commit("setModuleFlux", null);
    commit("setModuleCurrent", null);
    commit("setLuminaireFlux", null);
    commit("setLor", 100);
    commit("setModuleTa", 25);
    commit("setModuleRTH", null);
    commit("setModuleTemperature", null);
  },
};

function calculateParametersCheck(m, module) {
  if (!m.calculationResult && !module.calculationResult) return false;
  return (
    m.calculationResult.luminousFluxPerModule ===
      module.calculationResult.luminousFluxPerModule &&
    m.calculationResult.operatingCurrent ===
      module.calculationResult.operatingCurrent &&
    m.calculationResult.temperature === module.calculationResult.temperature
  );
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
